/** @file This file implements a filter manager for submissions. */
import { always } from 'purify-ts';
import { Maybe } from '@codegrade/maybe';
import * as utils from '@codegrade/utils';
import * as models from '@/models';
import { SectionsStore } from '@/store/modules/sections';
import { maybeParseBool, parseBool } from './boolean';
import { coerceToString } from './string';
const SORT_BY_OPTS = [
    'assignee',
    'user',
    'formattedCreatedAt',
    'formatted_created_at',
    'createdAt',
    'created_at',
    'grade',
    'rubric',
    'sections',
];
/**
 * Get the name of all the sections for a given user submission.
 *
 * @param sub The submission to get the sections for.
 * @returns A list of the section names if possible.
 */
function getSectionNames(sub) {
    return sub.maybeAssignment.map(assignment => SectionsStore.getUserSections()(assignment.courseId, sub.userId).map(section => section.name));
}
/**
 * Return a filtered list of submissions based on a text provided by the user.
 *
 * Sections will be preserved if the username, grade, created at, assignee,
 * group members or section name matches the provided string.
 *
 * @param submissions The submissions to filter.
 * @param mine Whether to preserve submissions made by the user.
 * @param userId The id of the user.
 * @param filter The string to filter on.
 * @param getUserSections A function that should return all the sections for a given user.
 * @param override An override function that allows to override the filtering for
 * certain submissions.
 * @returns The submissions matching the filter.
 */
function filterSubmissions(submissions, mine, userId, filter, getUserSections, override = always(false)) {
    if (!IS_PRODUCTION) {
        utils.assert.AssertionError.assert(submissions.every(sub => sub instanceof models.Submission), 'Not all submissions where real submission objects');
    }
    const l = new Set();
    const filterAssignee = mine && submissions.some(s => s.assigneeId === userId);
    return submissions.filter(item => {
        if (override(item))
            return true;
        if (filterAssignee && (item.assigneeId == null || item.assigneeId !== userId)) {
            return false;
        }
        if (!filter) {
            return true;
        }
        const user = item.user;
        const sections = getUserSections(user).map(section => section.name.toLocaleLowerCase());
        const terms = [
            item.user
                .getDisplayName(item.assignment)
                .mapOrDefault(name => name.toLocaleLowerCase(), 'unknown'),
            coerceToString(item.formattedGrade.extract()),
            item.formattedCreatedAt,
            item.readableCreatedAt.toLocaleLowerCase(),
            item.assignee.mapOrDefault(a => a
                .getDisplayName(undefined)
                .mapOrDefault(name => name.toLocaleLowerCase(), 'unknown'), '-'),
            ...sections,
            ...user
                .getGroupMembers()
                .map(member => member
                .getDisplayName(item.assignment)
                .mapOrDefault(name => name.toLocaleLowerCase(), 'unknown')),
        ];
        if (item.isLate) {
            terms.push('late');
        }
        if (item.user != null && item.user.isGroup) {
            terms.push('group');
        }
        else if (item.user != null) {
            terms.push('personal');
        }
        const out = filter
            .toLocaleLowerCase()
            .split(' ')
            .every(word => terms.some(term => term.includes(word)));
        if (out) {
            l.add(item.userId);
        }
        return out;
    });
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function getRubricResultLength(x) {
    return x.manuallyMarkedRows.length;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function getSubmissionsKey(sub, sortBy) {
    switch (sortBy) {
        case 'formattedCreatedAt':
        case 'formatted_created_at':
        case 'createdAt':
        case 'created_at':
            return sub.createdAt;
        case 'rubric': {
            return sub.rubricResult.join().map(getRubricResultLength);
        }
        case 'assignee': {
            return sub.assignee.chain(user => user.getDisplayName(undefined));
        }
        case 'user': {
            return sub.user.getDisplayName(sub.assignment);
        }
        case 'grade': {
            return sub.maybeGrade.map(g => g.toNumber());
        }
        case 'sections': {
            return getSectionNames(sub);
        }
        default:
            utils.assert.AssertionError.typeAssert(sortBy);
            return 0;
    }
}
/** A manager to filter and sort submissions. */
export default class FilterSubmissionsManager {
    currentSubmissionId;
    userId;
    courseId;
    /**
     * @param currentSubmissionId This submission will always be included when filtering.
     * @param userId The id of the current user.
     * @param courseId The id of the current course.
     */
    constructor(currentSubmissionId, userId, courseId) {
        this.currentSubmissionId = currentSubmissionId;
        this.userId = userId;
        this.courseId = courseId;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static getOptionsFromRoute(route) {
        const query = route.query;
        const querySortBy = query.sortBy;
        const sortBy = utils.array
            .findMaybe(validName => validName === querySortBy, SORT_BY_OPTS)
            .orDefault('user');
        return {
            mine: maybeParseBool(query.mine).extract(),
            filter: query.search,
            sortBy,
            asc: parseBool(query.sortAsc, true),
        };
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static optionsToRouteQuery(opts) {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        function keepIfNot(x, ...vals) {
            return vals.includes(x) ? undefined : coerceToString(x);
        }
        return {
            mine: keepIfNot(opts.mine, undefined),
            search: keepIfNot(opts.filter, '', null, undefined),
            sortBy: keepIfNot(opts.sortBy, 'user', null, undefined),
            sortAsc: keepIfNot(opts.asc, true, null, undefined),
        };
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static sort(submissions, { sortBy, asc }) {
        const key = sortBy ?? 'user';
        return utils.sort.sortBy(submissions, sub => [getSubmissionsKey(sub, key), getSubmissionsKey(sub, 'user')], {
            reverse: !asc,
            justCmpNothingIsPerKey: [
                key === 'user' || key === 'assignee' ? utils.compare.Order.LT : undefined,
                utils.compare.Order.LT,
            ],
        });
    }
    /**
     * Filters a set of submissions based on the options provided to the filter
     * manager.
     *
     * @param submissions The submissions to filter.
     * @param opts The filtering options.
     * @returns The filtered submissions.
     */
    filter(submissions, opts) {
        const override = this.currentSubmissionId == null
            ? // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
                () => false
            : // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
                // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
                sub => sub.id === this.currentSubmissionId;
        const getUserSections = Maybe.fromNullable(this.courseId).mapOrDefault(courseId => 
        /**
         * A function to return the sections for a user.
         *
         * @param user The user of which the sections should be returned.
         * @returns The sections of the given user for the closed over course.
         */
        (user) => SectionsStore.getUserSections()(courseId, user.id), () => []);
        return filterSubmissions(submissions, opts.mine ?? false, this.userId, opts.filter ?? '', getUserSections, override);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    filterAndSort(submissions, opts) {
        const filtered = this.filter(submissions, opts);
        return FilterSubmissionsManager.sort(filtered, opts);
    }
}
